import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowRight,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Sparkles,
  Layers,
  Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { renderSafeHtml } from '../components/RichTextEditor';

const DIFFICULTY_LABELS = { easy: 'سهلة', medium: 'متوسطة', hard: 'صعبة' };
const DIFFICULTY_COLORS = {
  easy: 'bg-green-100 text-green-700',
  medium: 'bg-amber-100 text-amber-700',
  hard: 'bg-red-100 text-red-700',
};

const TopicAttemptResults = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const { token, isApprovedStudent } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Auth is enforced by ApprovedStudentRoute; just wait until we have a token.
    if (!token) return;
    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, attemptId]);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/topic-attempts/${attemptId}/results`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (r.ok) {
        setData(await r.json());
      } else {
        toast.error('تعذّر تحميل النتيجة');
        navigate('/student/exams');
      }
    } catch {
      toast.error('حدث خطأ');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-[#EF9F27]" />
      </div>
    );
  }

  if (!data) return null;

  const pct = data.total_points ? Math.round(((data.score || 0) / data.total_points) * 100) : 0;
  const passed = pct >= 50;

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      <header className="bg-[#FFFFFF] border-b border-[#E8DFCF] sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/student/exams" className="flex items-center gap-3">
              <img src="/logo.png" alt="Logo" className="w-10 h-10 rounded-lg object-cover border border-[#E8DFCF]" />
              <span className="text-xl font-bold text-[#EF9F27]">المعتمد في اللغة العربية</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate('/student/exams')} className="text-[#6B4C2A] mb-4">
          <ArrowRight className="w-4 h-4 ml-2" /> العودة
        </Button>

        <Card className="border-[#E8DFCF] mb-6">
          <CardHeader className="border-b border-[#E8DFCF] bg-[#FFFFFF]">
            <CardTitle className="text-xl font-bold text-[#412402] flex items-center gap-2">
              <Layers className="w-5 h-5 text-[#EF9F27]" />
              {data.topic_title}
            </CardTitle>
            <p className="text-sm text-[#412402]/60 mt-1">المحاولة #{data.attempt_number}</p>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {data.show_score && data.score !== null && (
                <div className={`rounded-lg p-4 text-center ${passed ? 'bg-green-50' : 'bg-red-50'}`}>
                  <p className="text-xs text-[#412402]/60 mb-1">الدرجة</p>
                  <p className={`text-2xl font-bold ${passed ? 'text-green-700' : 'text-red-700'}`}>
                    {data.score}/{data.total_points}
                  </p>
                  <p className={`text-sm ${passed ? 'text-green-700' : 'text-red-700'}`}>{pct}%</p>
                </div>
              )}
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-green-700">{data.correct_count}</p>
                <p className="text-xs text-[#412402]/60">إجابات صحيحة</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <XCircle className="w-6 h-6 text-red-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-red-700">{data.wrong_count}</p>
                <p className="text-xs text-[#412402]/60">إجابات خاطئة</p>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 text-center">
                <AlertTriangle className="w-6 h-6 text-amber-600 mx-auto mb-1" />
                <p className="text-2xl font-bold text-amber-700">{data.violation_count}</p>
                <p className="text-xs text-[#412402]/60">المخالفات</p>
              </div>
            </div>

            {/* Old-question review/re-attempt is gated by the re-attempt `tries` cap
                (review_locked). A NEW-question round stays available while the bank
                still has fresh questions (has_new_questions), independent of that cap. */}
            <div className="space-y-3">
              {data.review_locked && (
                <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                  <Lock className="w-4 h-4 shrink-0 mt-0.5 text-amber-700" />
                  <span>
                    {!data.folder_unlocked
                      ? 'هذا الملف مقفل — تظهر لك درجتك فقط. أعد فتح المجلد من بنك الأسئلة.'
                      : 'تظهر لك درجتك فقط — انتهت مراجعة هذا الامتحان.'}
                  </span>
                </div>
              )}
              {data.folder_unlocked && (
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Buttons show while the folder is open. Re-attempt is DISABLED once
                      the tries are used up (review then shows only the mark); the
                      new-question button stays clickable while fresh questions remain. */}
                  <Button
                    disabled={data.reattempt_locked}
                    onClick={() => navigate(`/student/topic/${data.topic_id}/attempt?reattempt=${data.attempt_id}`)}
                    variant="outline"
                    className="flex-1 border-amber-700 text-amber-700 hover:bg-amber-50 disabled:opacity-40"
                    data-testid="reattempt-btn"
                    title={data.reattempt_locked ? 'انتهت محاولات إعادة الأسئلة القديمة' : 'إعادة نفس الأسئلة'}
                  >
                    <RefreshCw className="w-4 h-4 ml-2" />
                    إعادة المحاولة (نفس الأسئلة)
                  </Button>
                  {/* New questions: clickable whenever fresh questions remain. */}
                  {data.has_new_questions && (
                    <Button
                      onClick={() => navigate(`/student/topic/${data.topic_id}/attempt`)}
                      className="flex-1 bg-amber-700 hover:bg-amber-800"
                      data-testid="new-attempt-btn"
                    >
                      <Sparkles className="w-4 h-4 ml-2" />
                      محاولة جديدة (أسئلة جديدة)
                    </Button>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {!data.review_locked && data.show_correct_answers && data.questions && data.questions.length > 0 && (
          <Card className="border-[#E8DFCF]">
            <CardHeader className="border-b border-[#E8DFCF]">
              <CardTitle className="text-base font-semibold text-[#412402]">مراجعة الأسئلة</CardTitle>
            </CardHeader>
            <CardContent className="p-0 divide-y divide-slate-100">
              {data.questions.map((q, idx) => (
                <div key={q.id} className={`p-4 ${q.is_correct ? 'bg-green-50/30' : q.student_answer ? 'bg-red-50/30' : 'bg-amber-50/30'}`}>
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-sm font-bold text-[#412402]">س{idx + 1}</span>
                    <Badge variant="outline" className="text-xs">{q.points} درجة</Badge>
                    {q.is_correct ? (
                      <Badge className="bg-green-100 text-green-700">صحيحة</Badge>
                    ) : q.student_answer ? (
                      <Badge className="bg-red-100 text-red-700">خاطئة</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700">لم تجب</Badge>
                    )}
                  </div>
                  <p dir="auto" className="text-[#412402] font-medium mb-3 leading-relaxed whitespace-pre-wrap break-words" dangerouslySetInnerHTML={renderSafeHtml(q.text)} />

                  {q.question_type === 'multiple_choice' && (
                    <div className="space-y-1 text-sm">
                      {(q.options || []).map(opt => {
                        const isStudent = q.student_answer === opt.id;
                        const isCorrect = q.correct_answer === opt.id;
                        return (
                          <div key={opt.id} className={`flex items-start gap-2 p-2 rounded ${
                            isCorrect ? 'bg-green-100 text-green-800' :
                            isStudent && !isCorrect ? 'bg-red-100 text-red-800' :
                            'text-[#412402]/70'
                          }`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-current shrink-0 mt-2" />
                            <span dir="auto" className="flex-1 min-w-0 break-words" dangerouslySetInnerHTML={renderSafeHtml(opt.text)} />
                            {isStudent && <Badge variant="outline" className="text-xs shrink-0">إجابتك</Badge>}
                            {isCorrect && <Badge className="text-xs bg-green-200 text-green-800 shrink-0">الصحيحة</Badge>}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {q.question_type === 'true_false' && (
                    <div className="text-sm space-y-1">
                      <p>إجابتك: <span className={q.is_correct ? 'text-green-700 font-bold' : 'text-red-700 font-bold'}>
                        {q.student_answer === 'true' ? 'صح' : q.student_answer === 'false' ? 'خطأ' : '—'}
                      </span></p>
                      {!q.is_correct && q.correct_answer && (
                        <p>الإجابة الصحيحة: <span className="text-green-700 font-bold">
                          {q.correct_answer === 'true' ? 'صح' : 'خطأ'}
                        </span></p>
                      )}
                    </div>
                  )}

                  {q.question_type === 'short_answer' && (
                    <div className="text-sm space-y-1">
                      <p>إجابتك: <span className={q.is_correct ? 'text-green-700 font-bold' : 'text-red-700'}>
                        {q.student_answer || '—'}
                      </span></p>
                      {!q.is_correct && q.correct_answer && (
                        <p>الإجابة الصحيحة: <span className="text-green-700 font-bold">{q.correct_answer}</span></p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default TopicAttemptResults;
